// src/pages/SignIn.tsx
import Seo from "../components/Seo";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  useLoginMutation,
  useLearnerLoginMutation,
} from "../app/api/authApi";
import { useAdminLoginMutation } from "../app/api/adminAuthApi";

// ── Slice actions ──
import { setCredentials } from "../app/features/authSlice";
import { setAdminCredentials } from "../app/features/adminAuthSlice";

// ── Selectors — if already logged in, skip straight to dashboard ──
import { selectAdminUser } from "../app/features/adminAuthSlice";
import { selectUser } from "../app/features/authSlice";

import { Bot, Briefcase, Coins, Hammer, Mic } from "lucide-react";
import Ico from "../components/Ico";
/* ─────────────────────────────────────────────────────────────────
   ROLE → DESTINATION MAP
   Extend this as new roles are added.
───────────────────────────────────────────────────────────────── */
const ROLE_DESTINATIONS: Record<string, string> = {
  super_admin: "/admin/dashboard",
  admin: "/admin/dashboard",
  staff: "/admin/dashboard",
  student: "/user/learn",
  parent: "/user/dashboard",
  tutor: "/admin/reviews",
  user: "/user/dashboard",
};

function getDestination(role: string, childId?: string): string {
  // A learner session opens straight onto the child's own lessons
  if (role === "learner" && childId) return `/user/learn/${childId}`;
  return ROLE_DESTINATIONS[role] ?? "/dashboard";
}

/** No "@" means a learner username (children 9+ have no email). */
const isLearnerUsername = (v: string) => !!v.trim() && !v.includes("@");

/* ─────────────────────────────────────────────────────────────────
   SMALL UI ATOMS
───────────────────────────────────────────────────────────────── */

function FSLMark() {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/images/logo.png" alt="" className="h-9 w-9 rounded-[10px]" />
      <div>
        <p className="text-[13px] font-bold leading-tight tracking-[-0.03em] text-[#16213e]">
          Future Smart Lab
        </p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#a6adbf]">
          Innovation Academy
        </p>
      </div>
    </div>
  );
}

interface InputFieldProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  disabled?: boolean;
  rightElement?: React.ReactNode;
}

function InputField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  error,
  disabled,
  rightElement,
}: InputFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-semibold text-[#3a3750]">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          className={[
            "w-full rounded-2xl border bg-white px-4 py-3.5 text-sm text-[#16213e] placeholder:text-[#a6adbf]",
            "outline-none transition-all",
            "focus:border-[#1b2d5e] focus:ring-4 focus:ring-[#1b2d5e]/12",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error
              ? "border-[#e57373] focus:border-[#e57373] focus:ring-[#e57373]/10"
              : "border-[#dde2ec]",
            rightElement ? "pr-12" : "",
          ].join(" ")}
        />
        {rightElement && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-4">
            {rightElement}
          </div>
        )}
      </div>
      {error && (
        <p className="flex items-center gap-1.5 text-xs text-[#e57373]">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#e57373]" />
          {error}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   DECORATIVE LEFT PANEL
───────────────────────────────────────────────────────────────── */

const QUOTES = [
  {
    text: "A child who builds will create the systems others live inside.",
    attr: "Future Smart Lab",
  },
  {
    text: "The next wave of Nigerian founders is currently between 6 and 18.",
    attr: "FSL Programme Vision",
  },
  {
    text: "Not just a coder. A founder in the making.",
    attr: "FSL Mission",
  },
];

function LeftPanel() {
  const [qi, setQi] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setQi((i) => (i + 1) % QUOTES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const q = QUOTES[qi];

  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-[#16213e] p-10 lg:flex lg:w-[46%]">
      {/* Grid texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(27,45,94,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(27,45,94,0.07) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px",
        }}
      />

      {/* Radial glow blobs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-16 -top-16 h-72 w-72 rounded-full bg-[#1b2d5e]/20 blur-3xl" />
        <div className="absolute -bottom-12 right-4 h-56 w-56 rounded-full bg-[#efb0a4]/15 blur-3xl" />
      </div>

      {/* Top logo */}
      <div className="relative z-10">
        <div className="flex items-center gap-2.5">
          <img src="/images/logo.png" alt="" className="h-10 w-10 rounded-[12px]" />
          <div>
            <p className="text-sm font-bold tracking-[-0.03em] text-white">
              Future Smart Lab
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
              Innovation Academy
            </p>
          </div>
        </div>
      </div>

      {/* Formation strands */}
      <div className="relative z-10 space-y-3">
        {[
          { icon: Hammer, label: "Technology & Building" },
          { icon: Bot, label: "Applied AI" },
          { icon: Briefcase, label: "Venture & Entrepreneurship" },
          { icon: Mic, label: "Leadership & Public Speaking" },
          { icon: Coins, label: "Financial Literacy" },
        ].map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm"
          >
            <span className="text-base"><Ico icon={s.icon} /></span>
            <span className="text-sm font-medium text-white/75">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Rotating quote */}
      <div className="relative z-10">
        <div className="rounded-[20px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
          <p
            key={qi}
            className="text-[15px] font-medium leading-7 text-white/85"
            style={{ animation: "fadeSlideIn 0.5s ease" }}
          >
            "{q.text}"
          </p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-[#1b2d5e]">
            — {q.attr}
          </p>
        </div>

        {/* Dot indicators */}
        <div className="mt-4 flex gap-1.5">
          {QUOTES.map((_, i) => (
            <button
              key={i}
              onClick={() => setQi(i)}
              className={[
                "h-1.5 rounded-full transition-all",
                i === qi ? "w-6 bg-[#1b2d5e]" : "w-1.5 bg-white/20",
              ].join(" ")}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SIGN IN FORM
───────────────────────────────────────────────────────────────── */

export default function SignIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // If already authenticated, redirect immediately
  const adminUser = useSelector(selectAdminUser);
  const publicUser = useSelector(selectUser);

  // Go back to the page that asked for sign-in, but only inside this
  // account's own portal (a parent sent here from /admin goes to /user)
  const fromState = (location.state as any)?.from;
  const returnTo = useCallback(
    (role: string, isAdminPortalUser: boolean, childId?: string) => {
      const portal = isAdminPortalUser ? "/admin/" : "/user/";
      return typeof fromState?.pathname === "string" && fromState.pathname.startsWith(portal)
        ? `${fromState.pathname}${fromState.search ?? ""}${fromState.hash ?? ""}`
        : getDestination(role, childId);
    },
    [fromState],
  );

  useEffect(() => {
    if (adminUser?.role) {
      navigate(returnTo(adminUser.role, true), { replace: true });
    } else if (publicUser?.role) {
      navigate(returnTo(publicUser.role, false, publicUser.childId), {
        replace: true,
      });
    }
  }, [adminUser, publicUser, navigate, returnTo]);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [serverError, setServerError] = useState("");

  // RTK Query mutations
  const [publicLogin, { isLoading: publicLoading }] = useLoginMutation();
  const [adminLogin, { isLoading: adminLoading }] = useAdminLoginMutation();
  const [learnerLogin, { isLoading: learnerLoading }] =
    useLearnerLoginMutation();
  const isLoading = publicLoading || adminLoading || learnerLoading;

  // ── Validation ──
  const validate = () => {
    const errs: typeof fieldErrors = {};
    // eslint-disable-next-line no-useless-escape
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!email.trim()) errs.email = "Email or learner username is required";
    else if (isLearnerUsername(email)) {
      // a learner username: the server checks it
    } else if (!emailRegex.test(email.trim()))
      errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";
    else if (password.length < 6)
      errs.password = "Password must be at least 6 characters";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    // Learner (child 9+) signing in with the username their parent set up
    if (isLearnerUsername(email)) {
      try {
        const { user, token } = await learnerLogin({
          username: email.trim().toLowerCase(),
          password,
        }).unwrap();
        dispatch(setCredentials({ token, user }));
        navigate(getDestination(user.role, user.childId), { replace: true });
      } catch (err: any) {
        setServerError(
          err?.data?.error || "That username and password don't match.",
        );
      }
      return;
    }

    const credentials = { email: email.trim().toLowerCase(), password };

    // Strategy: try admin login first (it will reject non-admin roles with 403).
    // If that fails with 403, try the public login.
    // This avoids exposing separate login pages while still routing correctly.
    try {
      let result: { user: { role: string }; token: string } | null = null;
      let isAdminPortalUser = false;

      try {
        const adminResult = await adminLogin(credentials).unwrap();
        result = adminResult;
        isAdminPortalUser = true;
      } catch (adminErr: any) {
        // 403 = valid user but not an admin-portal role → try public login
        if (
          adminErr?.status === 403 ||
          adminErr?.data?.error?.includes("portal")
        ) {
          const publicResult = await publicLogin(credentials).unwrap();
          result = publicResult;
          isAdminPortalUser = false;
        } else {
          // 401 or other error — surface it
          throw adminErr;
        }
      }

      if (!result) throw new Error("No result returned");

      const { user, token } = result;

      // Store credentials in the correct slice
      if (isAdminPortalUser) {
        dispatch(setAdminCredentials({ token, user: user as any }));
      } else {
        dispatch(setCredentials({ token, user: user as any }));
      }

      navigate(returnTo(user.role, isAdminPortalUser, (user as { childId?: string }).childId), {
        replace: true,
      });
    } catch (err: any) {
      const msg =
        err?.data?.error ||
        err?.error ||
        "Invalid email or password. Please try again.";
      setServerError(msg);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Seo title="Sign in" description="Sign in to your Future Smart Lab account." path="/login" noindex />
      <LeftPanel />

      {/* ── Right: form panel ── */}
      <div className="flex flex-1 flex-col items-center justify-center bg-[#f7f8fb] px-5 py-10 sm:px-8">
        {/* Mobile logo */}
        <div className="mb-8 lg:hidden">
          <FSLMark />
        </div>

        <div className="w-full max-w-[420px]">
          {/* Card */}
          <div className="rounded-[28px] border border-[#dde2ec] bg-white p-7 shadow-[0_20px_60px_rgba(27,45,94,0.09)] sm:p-8">
            {/* Header */}
            <div className="mb-7">
              <div className="hidden lg:block">
                <FSLMark />
              </div>
              <h1 className="mt-5 text-[1.6rem] font-bold leading-tight tracking-[-0.04em] text-[#16213e]">
                Welcome back
              </h1>
              <p className="mt-1.5 text-sm leading-6 text-[#5f6780]">
                Sign in to your account to continue
              </p>
            </div>

            {/* Server error */}
            {serverError && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#fde0d9] bg-[#fff5f3] px-4 py-3.5">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#efb0a4] text-[11.5px] font-bold text-white">
                  !
                </span>
                <p className="text-sm leading-5 text-[#b94a3a]">
                  {serverError}
                </p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <InputField
                id="email"
                label="Email or learner username"
                type="text"
                value={email}
                onChange={(v) => {
                  setEmail(v);
                  if (fieldErrors.email)
                    setFieldErrors((p) => ({ ...p, email: undefined }));
                  setServerError("");
                }}
                placeholder="you@example.com or ada.482"
                autoComplete="username"
                error={fieldErrors.email}
                disabled={isLoading}
              />

              <InputField
                id="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(v) => {
                  setPassword(v);
                  if (fieldErrors.password)
                    setFieldErrors((p) => ({ ...p, password: undefined }));
                  setServerError("");
                }}
                placeholder="Enter your password"
                autoComplete="current-password"
                error={fieldErrors.password}
                disabled={isLoading}
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-[#8189a0] transition hover:text-[#4a44b0]"
                    tabIndex={-1}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                        />
                        <circle
                          cx="8"
                          cy="8"
                          r="2"
                          stroke="currentColor"
                          strokeWidth="1.3"
                        />
                        <path
                          d="M2 2l12 12"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                        />
                        <circle
                          cx="8"
                          cy="8"
                          r="2"
                          stroke="currentColor"
                          strokeWidth="1.3"
                        />
                      </svg>
                    )}
                  </button>
                }
              />

              {/* Forgot password */}
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-[#1b2d5e] transition hover:text-[#4a44b0]"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className={[
                  "relative mt-1 flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5",
                  "text-sm font-semibold text-white",
                  "bg-[#1b2d5e] shadow-[0_8px_24px_rgba(27,45,94,0.35)]",
                  "transition-all hover:translate-y-[-1px] hover:bg-[#24397a] hover:shadow-[0_12px_32px_rgba(27,45,94,0.40)]",
                  "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0",
                ].join(" ")}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                      />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-[#ebeff7]" />
              <span className="text-xs text-[#a6adbf]">or</span>
              <div className="h-px flex-1 bg-[#ebeff7]" />
            </div>

            {/* Register CTA — for public users only */}
            <p className="text-center text-sm text-[#5f6780]">
              New parent or student?{" "}
              <Link
                to="/register"
                className="font-semibold text-[#1b2d5e] transition hover:text-[#4a44b0]"
              >
                Create an account
              </Link>
            </p>
          </div>

          {/* Footer note */}
          <p className="mt-6 text-center text-[12.5px] leading-5 text-[#a6adbf]">
            Academy staff sign in here too — your role determines your access.
            <br />
            Contact your super-admin if you have login issues.
          </p>
        </div>
      </div>
    </div>
  );
}
